import {AfterViewInit, Directive, ElementRef, Renderer2} from '@angular/core';

interface AtlasButtonSemantic {
  key: string;
  icon: string;
  label: string;
  order?: number;
  sortable?: boolean;
  toolbarOrder?: number;
}

@Directive({
  selector: 'button:not([data-atlas-ui-off]), a:not([data-atlas-ui-off])',
})
export class AtlasUiButtonDirective implements AfterViewInit {
  private readonly controlSelector: string = 'button, a, input[type="button"], input[type="submit"], input[type="reset"], [role="button"]';
  private readonly semantics: AtlasButtonSemantic[] = [
    {key: 'count', icon: 'fa-list-ol', label: 'Total count', toolbarOrder: 90},
    {key: 'detail', icon: 'fa-info-circle', label: 'View details', order: 10, sortable: true},
    {key: 'view', icon: 'fa-eye', label: 'View', order: 10, sortable: true},
    {key: 'info', icon: 'fa-info-circle', label: 'View information', order: 10, sortable: true},
    {key: 'add', icon: 'fa-plus', label: 'Add', order: 5, sortable: true, toolbarOrder: 40},
    {key: 'create', icon: 'fa-plus', label: 'Create', order: 5, sortable: true, toolbarOrder: 40},
    {key: 'edit', icon: 'fa-pencil', label: 'Edit', order: 20, sortable: true},
    {key: 'update', icon: 'fa-pencil', label: 'Update', order: 20, sortable: true},
    {key: 'copy', icon: 'fa-copy', label: 'Copy', order: 30, sortable: true},
    {key: 'history', icon: 'fa-history', label: 'View history', order: 40, sortable: true},
    {key: 'order', icon: 'fa-sort', label: 'Change order', order: 50, sortable: true},
    {key: 'download', icon: 'fa-download', label: 'Download', toolbarOrder: 60},
    {key: 'export', icon: 'fa-file-excel-o', label: 'Export', toolbarOrder: 60},
    {key: 'upload', icon: 'fa-upload', label: 'Upload', toolbarOrder: 50},
    {key: 'import', icon: 'fa-upload', label: 'Import', toolbarOrder: 50},
    {key: 'search', icon: 'fa-search', label: 'Search', toolbarOrder: 20},
    {key: 'filter', icon: 'fa-filter', label: 'Filter', toolbarOrder: 10},
    {key: 'reset', icon: 'fa-refresh', label: 'Reset', toolbarOrder: 30},
    {key: 'refresh', icon: 'fa-refresh', label: 'Refresh', toolbarOrder: 30},
    {key: 'retry', icon: 'fa-repeat', label: 'Retry'},
    {key: 'save', icon: 'fa-check', label: 'Save'},
    {key: 'approve', icon: 'fa-check-circle', label: 'Approve'},
    {key: 'reject', icon: 'fa-ban', label: 'Reject'},
    {key: 'assign', icon: 'fa-user-plus', label: 'Assign'},
    {key: 'publish', icon: 'fa-cloud-upload', label: 'Publish'},
    {key: 'unpublish', icon: 'fa-cloud-download', label: 'Unpublish'},
    {key: 'activate', icon: 'fa-toggle-on', label: 'Activate'},
    {key: 'deactivate', icon: 'fa-toggle-off', label: 'Deactivate'},
    {key: 'start', icon: 'fa-play', label: 'Start'},
    {key: 'stop', icon: 'fa-stop', label: 'Stop'},
    {key: 'next', icon: 'fa-chevron-right', label: 'Next'},
    {key: 'previous', icon: 'fa-chevron-left', label: 'Previous'},
    {key: 'back', icon: 'fa-arrow-left', label: 'Back'},
    {key: 'close', icon: 'fa-times', label: 'Close'},
    {key: 'cancel', icon: 'fa-times', label: 'Cancel'},
    {key: 'delete', icon: 'fa-trash', label: 'Delete', order: 90, sortable: true},
    {key: 'remove', icon: 'fa-trash', label: 'Remove', order: 90, sortable: true},
    {key: 'action', icon: 'fa-cog', label: 'Action'},
  ];

  constructor(private elementRef: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    Promise.resolve().then(() => this.enhanceButton());
  }

  private enhanceButton(): void {
    const element: HTMLElement = this.elementRef.nativeElement;
    const semantic: AtlasButtonSemantic = this.resolveSemantic(element);
    const tableCell: HTMLElement = this.findAncestor(element, ['TD', 'TH']);

    if (!this.shouldEnhance(element, tableCell, semantic)) {
      return;
    }

    this.renderer.addClass(element, 'atlas-ui-button');

    if (semantic) {
      this.renderer.addClass(element, 'atlas-action-' + semantic.key);
      this.renderer.setAttribute(element, 'data-atlas-action', semantic.key);
    }

    this.completeAccessibleName(element, semantic);
    this.completeIcon(element, semantic);
    this.markContext(element, semantic, tableCell);
  }

  private completeAccessibleName(element: HTMLElement, semantic: AtlasButtonSemantic): void {
    const visibleText: string = this.getVisibleText(element);
    const existingTitle: string = (element.getAttribute('title') || '').trim();
    const existingAriaLabel: string = (element.getAttribute('aria-label') || '').trim();
    const descendantLabel: string = this.getDescendantAccessibleName(element);
    const label: string = existingAriaLabel || existingTitle || visibleText || descendantLabel || (semantic && semantic.label) || 'Open action';

    if (!existingTitle) {
      this.renderer.setAttribute(element, 'title', label);
    }

    if (!existingAriaLabel) {
      this.renderer.setAttribute(element, 'aria-label', label);
    }
  }

  private completeIcon(element: HTMLElement, semantic: AtlasButtonSemantic): void {
    const existingIcon: HTMLElement = element.querySelector(
      '.fa, .pi, .glyphicon, .material-icons, .ui-button-icon, .navbar-toggle-icon, .px-nav-toggle-arrow, .caret, svg, img',
    ) as HTMLElement;

    if (existingIcon) {
      this.renderer.addClass(existingIcon, 'atlas-ui-button-icon');
      if (semantic && semantic.key === 'count' && existingIcon.classList.contains('fa-list-ol')) {
        this.renderer.addClass(existingIcon, 'atlas-count-icon');
      }
      return;
    }

    if (!semantic || this.hasVisualGlyph(element) || element.classList.contains('close')) {
      return;
    }

    const icon: HTMLElement = this.renderer.createElement('i');
    this.renderer.addClass(icon, 'fa');
    this.renderer.addClass(icon, semantic.icon);
    this.renderer.addClass(icon, 'atlas-ui-button-icon');
    if (semantic.key === 'count') {
      this.renderer.addClass(icon, 'atlas-count-icon');
    }
    this.renderer.setAttribute(icon, 'aria-hidden', 'true');
    this.renderer.insertBefore(element, icon, element.firstChild);
  }

  private markContext(element: HTMLElement, semantic: AtlasButtonSemantic, tableCell: HTMLElement): void {
    if (semantic && semantic.key === 'count') {
      this.renderer.addClass(element, 'atlas-count-indicator');
    }

    if (tableCell) {
      this.renderer.addClass(tableCell, 'atlas-action-cell');
      const actionContainer: HTMLElement = this.findOrderingContainer(tableCell);
      if (actionContainer && actionContainer !== tableCell) {
        this.renderer.addClass(actionContainer, 'atlas-action-toolbar');
      }
      this.normalizeSafeOrder(actionContainer || tableCell, false);
      return;
    }

    const toolbar: HTMLElement = this.findToolbar(element, semantic);
    if (toolbar) {
      this.renderer.addClass(toolbar, 'atlas-table-toolbar');
      this.normalizeSafeOrder(toolbar, true);
    }
  }

  private normalizeSafeOrder(container: HTMLElement, toolbarMode: boolean): void {
    if (!container) {
      return;
    }

    const units: HTMLElement[] = this.getActionUnits(container);
    if (units.length < 2 || units.length !== container.children.length) {
      return;
    }

    const orderedActions: Array<{element: HTMLElement; order: number; index: number}> = [];
    for (let index = 0; index < units.length; index++) {
      const controls: HTMLElement[] = this.getControls(units[index]);
      if (controls.length !== 1) {
        return;
      }

      const semantic: AtlasButtonSemantic = this.getControlSemantic(controls[0]);
      const order: number = toolbarMode ? semantic && semantic.toolbarOrder : semantic && semantic.order;

      if (!semantic || (toolbarMode ? typeof semantic.toolbarOrder !== 'number' : !semantic.sortable)) {
        return;
      }

      orderedActions.push({element: units[index], order: order, index: index});
    }

    orderedActions
      .sort((left, right) => left.order === right.order ? left.index - right.index : left.order - right.order)
      .forEach((action) => this.renderer.appendChild(container, action.element));
  }

  private resolveSemantic(element: HTMLElement): AtlasButtonSemantic {
    const text: string = this.normalizeText([
      element.textContent || '',
      (element as HTMLInputElement).value || '',
      element.getAttribute('title') || '',
      element.getAttribute('aria-label') || '',
      this.getDescendantAccessibleName(element),
    ].join(' '));
    const iconClasses: string = this.normalizeText(this.getIconClasses(element));

    if (this.isCountText(text, element.classList.contains('btn'))) {
      return this.findSemantic('count');
    }
    if (this.containsAny(text, ['unpublish', 'yayindan kaldir'])) {
      return this.findSemantic('unpublish');
    }
    if (this.containsAny(text, ['deactivate', 'pasiflestir'])) {
      return this.findSemantic('deactivate');
    }
    if (this.containsAny(text, ['delete', 'remove', 'sil', 'kaldir'])) {
      return this.findSemantic(this.containsAny(text, ['remove', 'kaldir']) ? 'remove' : 'delete');
    }
    if (this.containsAny(text, ['detail', 'details', 'detay', 'job detail', 'payload'])) {
      return this.findSemantic('detail');
    }
    if (this.containsAny(text, ['preview', 'view', 'goruntule', 'izle'])) {
      return this.findSemantic('view');
    }
    if (this.containsAny(text, ['information', 'info', 'bilgi'])) {
      return this.findSemantic('info');
    }
    if (this.containsAny(text, ['edit', 'duzenle'])) {
      return this.findSemantic('edit');
    }
    if (this.containsAny(text, ['update', 'guncelle'])) {
      return this.findSemantic('update');
    }
    if (this.containsAny(text, ['copy', 'clone', 'kopya', 'kopyala'])) {
      return this.findSemantic('copy');
    }
    if (this.containsAny(text, ['history', 'logs', 'log', 'gecmis'])) {
      return this.findSemantic('history');
    }
    if (this.containsAny(text, ['reorder', 'content order', 'change order', 'sirala', 'siralama'])) {
      return this.findSemantic('order');
    }
    if (this.containsAny(text, ['new', 'add', 'ekle'])) {
      return this.findSemantic('add');
    }
    if (this.containsAny(text, ['create', 'olustur'])) {
      return this.findSemantic('create');
    }
    if (this.containsAny(text, ['excel', 'export', 'disa aktar'])) {
      return this.findSemantic('export');
    }
    if (this.containsAny(text, ['download', 'indir'])) {
      return this.findSemantic('download');
    }
    if (this.containsAny(text, ['upload', 'yukle'])) {
      return this.findSemantic('upload');
    }
    if (this.containsAny(text, ['import', 'ice aktar'])) {
      return this.findSemantic('import');
    }
    if (this.containsAny(text, ['advanced search', 'search', 'ara'])) {
      return this.findSemantic('search');
    }
    if (this.containsAny(text, ['filter', 'filtre'])) {
      return this.findSemantic('filter');
    }
    if (this.containsAny(text, ['reset', 'clear', 'temizle', 'sifirla'])) {
      return this.findSemantic('reset');
    }
    if (this.containsAny(text, ['refresh', 'reload', 'yenile'])) {
      return this.findSemantic('refresh');
    }
    if (this.containsAny(text, ['retry', 'reprocess', 'tekrar'])) {
      return this.findSemantic('retry');
    }
    if (this.containsAny(text, ['save', 'submit', 'apply', 'kaydet', 'uygula'])) {
      return this.findSemantic('save');
    }
    if (this.containsAny(text, ['not approve', 'reject', 'reddet'])) {
      return this.findSemantic('reject');
    }
    if (this.containsAny(text, ['approve', 'validate', 'onayla', 'dogrula'])) {
      return this.findSemantic('approve');
    }
    if (this.containsAny(text, ['assign', 'ata'])) {
      return this.findSemantic('assign');
    }
    if (this.containsAny(text, ['publish', 'yayinla'])) {
      return this.findSemantic('publish');
    }
    if (this.containsAny(text, ['activate', 'aktiflestir'])) {
      return this.findSemantic('activate');
    }
    if (this.containsAny(text, ['start', 'baslat'])) {
      return this.findSemantic('start');
    }
    if (this.containsAny(text, ['stop', 'finish', 'bitir', 'durdur'])) {
      return this.findSemantic('stop');
    }
    if (this.containsAny(text, ['next', 'sonraki'])) {
      return this.findSemantic('next');
    }
    if (this.containsAny(text, ['previous', 'onceki'])) {
      return this.findSemantic('previous');
    }
    if (this.containsAny(text, ['back', 'geri'])) {
      return this.findSemantic('back');
    }
    if (this.containsAny(text, ['close', 'kapat'])) {
      return this.findSemantic('close');
    }
    if (this.containsAny(text, ['cancel', 'iptal'])) {
      return this.findSemantic('cancel');
    }

    if (this.containsAny(iconClasses, ['fa-trash', 'fa-minus-square'])) {
      return this.findSemantic('delete');
    }
    if (this.containsAny(iconClasses, ['fa-pencil', 'fa-edit'])) {
      return this.findSemantic('edit');
    }
    if (this.containsAny(iconClasses, ['fa-info-circle'])) {
      return this.findSemantic('detail');
    }
    if (this.containsAny(iconClasses, ['fa-eye'])) {
      return this.findSemantic('view');
    }
    if (this.containsAny(iconClasses, ['fa-plus'])) {
      return this.findSemantic('add');
    }
    if (this.containsAny(iconClasses, ['fa-copy', 'fa-clone'])) {
      return this.findSemantic('copy');
    }
    if (this.containsAny(iconClasses, ['fa-history'])) {
      return this.findSemantic('history');
    }
    if (this.containsAny(iconClasses, ['fa-refresh'])) {
      return this.findSemantic('refresh');
    }
    if (this.containsAny(iconClasses, ['fa-search'])) {
      return this.findSemantic('search');
    }

    return this.findSemantic('action');
  }

  private getVisibleText(element: HTMLElement): string {
    return (element.textContent || '').replace(/\s+/g, ' ').trim();
  }

  private getDescendantAccessibleName(element: HTMLElement): string {
    const labelledElement: HTMLElement = element.querySelector('[aria-label], [title]') as HTMLElement;
    if (!labelledElement) {
      return '';
    }

    return (labelledElement.getAttribute('aria-label') || labelledElement.getAttribute('title') || '').trim();
  }

  private getIconClasses(element: HTMLElement): string {
    const icon: HTMLElement = element.querySelector('.fa, .pi, .glyphicon, .material-icons') as HTMLElement;
    return icon ? icon.className : '';
  }

  private hasVisualGlyph(element: HTMLElement): boolean {
    const text: string = this.getVisibleText(element);
    return text === '×' || text === 'x' || text === '+' || text === '-' || text === '«' || text === '»';
  }

  private findSemantic(key: string): AtlasButtonSemantic {
    return this.semantics.find((semantic: AtlasButtonSemantic) => semantic.key === key);
  }

  private containsAny(value: string, candidates: string[]): boolean {
    return candidates.some((candidate: string) => {
      const escapedCandidate: string = candidate
        .trim()
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\s+/g, '\\s+');
      return new RegExp('(^|[^a-z0-9])' + escapedCandidate + '($|[^a-z0-9])').test(value);
    });
  }

  private normalizeText(value: string): string {
    return value
      .toLowerCase()
      .replace(/\u0307/g, '')
      .replace(/[ç]/g, 'c')
      .replace(/[ğ]/g, 'g')
      .replace(/[ı]/g, 'i')
      .replace(/[ö]/g, 'o')
      .replace(/[ş]/g, 's')
      .replace(/[ü]/g, 'u')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private isCountText(value: string, allowTotalColon: boolean): boolean {
    const recognizedCount: boolean = /^(?:total(?:\s*-\s*|\s+)count|total\s+item|toplam)(?:$|[^a-z0-9])/.test(value);
    return recognizedCount || (allowTotalColon && /^total\s*:/.test(value));
  }

  private findAncestor(element: HTMLElement, tagNames: string[]): HTMLElement {
    let current: HTMLElement = element.parentElement;
    while (current) {
      if (tagNames.indexOf(current.tagName) !== -1) {
        return current;
      }
      if (current.tagName === 'TABLE') {
        break;
      }
      current = current.parentElement;
    }
    return null;
  }

  private shouldEnhance(element: HTMLElement, tableCell: HTMLElement, semantic: AtlasButtonSemantic): boolean {
    if (element.tagName === 'BUTTON') {
      return true;
    }

    if (element.classList.contains('btn') || element.getAttribute('role') === 'button') {
      return true;
    }

    if (!tableCell) {
      return false;
    }

    const groupedAction: boolean = !!this.findAncestorBySelector(
      element, tableCell, '.btn-group, .btn-toolbar, [role="group"], [role="toolbar"]',
    );
    const actionColumn: boolean = this.isActionColumn(tableCell);

    return element.hasAttribute('data-toggle') || groupedAction ||
      (actionColumn && (!!element.querySelector('.fa, .pi, .glyphicon, .material-icons, svg') || !!(semantic && semantic.sortable)));
  }

  private isActionColumn(tableCell: HTMLElement): boolean {
    const row: HTMLElement = tableCell.parentElement;
    const table: HTMLElement = this.findAncestor(tableCell, ['TABLE']);
    if (!row || !table) {
      return false;
    }

    const cellIndex: number = Array.prototype.indexOf.call(row.children, tableCell);
    const headerRows: NodeListOf<HTMLElement> = table.querySelectorAll('thead tr');
    const headerRow: HTMLElement = headerRows.length ? headerRows[headerRows.length - 1] : null;
    const headerCell: HTMLElement = headerRow && headerRow.children[cellIndex] as HTMLElement;
    const headerText: string = this.normalizeText(headerCell ? headerCell.textContent || '' : '');

    return this.containsAny(headerText, ['action', 'actions', 'operation', 'operations', 'islem', 'islemler']);
  }

  private findOrderingContainer(tableCell: HTMLElement): HTMLElement {
    const candidates: HTMLElement[] = Array.prototype.slice.call(
      tableCell.querySelectorAll('.btn-toolbar, [role="toolbar"], .btn-group, [role="group"]'),
    );

    for (let index = candidates.length - 1; index >= 0; index--) {
      if (this.getActionUnits(candidates[index]).length >= 2) {
        return candidates[index];
      }
    }

    return tableCell;
  }

  private getActionUnits(container: HTMLElement): HTMLElement[] {
    return Array.prototype.slice.call(container.children).filter((child: HTMLElement) => this.getControls(child).length > 0);
  }

  private getControls(container: HTMLElement): HTMLElement[] {
    const controls: HTMLElement[] = [];
    if (container.matches && container.matches(this.controlSelector)) {
      controls.push(container);
    }

    Array.prototype.slice.call(container.querySelectorAll(this.controlSelector)).forEach((control: HTMLElement) => {
      if (controls.indexOf(control) === -1) {
        controls.push(control);
      }
    });
    return controls;
  }

  private getControlSemantic(control: HTMLElement): AtlasButtonSemantic {
    const key: string = control.getAttribute('data-atlas-action');
    return this.semantics.find((item: AtlasButtonSemantic) => item.key === key) || this.resolveSemantic(control);
  }

  private findToolbar(element: HTMLElement, semantic: AtlasButtonSemantic): HTMLElement {
    let current: HTMLElement = element.parentElement;
    let toolbar: HTMLElement = null;
    while (current && current.tagName !== 'BODY') {
      if (current.classList.contains('btn-toolbar') ||
        current.classList.contains('table-header') ||
        current.classList.contains('table-caption') ||
        current.classList.contains('ui-table-caption') ||
        current.getAttribute('role') === 'toolbar') {
        toolbar = current;
        break;
      }
      current = current.parentElement;
    }

    if (!toolbar || this.findAncestorBySelector(toolbar, null, '.modal, .modal-footer')) {
      return null;
    }

    if (toolbar.hasAttribute('data-atlas-table-toolbar') || this.hasTableToolbarContext(toolbar) ||
      (semantic && typeof semantic.toolbarOrder === 'number' && this.isSafeTableToolbar(toolbar))) {
      return toolbar;
    }

    return null;
  }

  private hasTableToolbarContext(toolbar: HTMLElement): boolean {
    let current: HTMLElement = toolbar;
    while (current && current.tagName !== 'BODY') {
      const context: string = this.normalizeText(
        ((current.className || '') + ' ' + (current.getAttribute('aria-label') || '')).replace(/[-_]/g, ' '),
      );
      if (this.containsAny(context, [
        'table header', 'table caption', 'ui table caption', 'table toolbar', 'table actions',
        'table filter', 'filter toolbar', 'list toolbar', 'list actions', 'advanced search', 'container h100px',
      ])) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private isSafeTableToolbar(toolbar: HTMLElement): boolean {
    const controls: HTMLElement[] = this.getControls(toolbar);
    return controls.length > 0 && controls.every((control: HTMLElement) => {
      const semantic: AtlasButtonSemantic = this.getControlSemantic(control);
      return !!semantic && typeof semantic.toolbarOrder === 'number';
    });
  }

  private findAncestorBySelector(element: HTMLElement, boundary: HTMLElement, selector: string): HTMLElement {
    let current: HTMLElement = element.parentElement;
    while (current && current !== boundary) {
      if (current.matches && current.matches(selector)) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }
}
